/**
 * Storefront search query capture.
 *
 * Listens for /search?q=... page loads, captures the query into a Shopify
 * cart attribute `_search_q_log` (JSON array of {q, ts}) so it propagates
 * to the next order's note_attributes. The Ads Manager's predictive-search
 * miner reads from there server-side to identify high-intent untapped
 * keywords.
 *
 * Why: Shopify storefront search analytics aren't exposed via Admin API for
 * non-Plus stores. Client-side capture is the lightweight unblock.
 *
 * Defensive throughout: every external call wrapped in try/catch, async
 * fetch fire-and-forget. Worst case: no capture happens, no UX impact.
 *
 * Companion: snippets/click-id-early-capture.liquid (already shipped, same pattern).
 */
(function () {
  'use strict';

  const COOKIE_NAME = '_search_q_log';
  const MAX_ENTRIES = 50; // soft cap on log size before truncation
  const COOKIE_TTL_DAYS = 7;
  const CART_ATTR_KEY = '_search_q_log';

  function readCookie(name) {
    try {
      const pairs = document.cookie ? document.cookie.split('; ') : [];
      for (let i = 0; i < pairs.length; i++) {
        const idx = pairs[i].indexOf('=');
        if (idx === -1) continue;
        const k = pairs[i].slice(0, idx);
        if (k === name) {
          try {
            return decodeURIComponent(pairs[i].slice(idx + 1));
          } catch (_e) {
            return null;
          }
        }
      }
    } catch (_e) {
      /* swallow */
    }
    return null;
  }

  function writeCookie(name, value) {
    try {
      const expires = new Date(Date.now() + COOKIE_TTL_DAYS * 86400000).toUTCString();
      document.cookie =
        name + '=' + encodeURIComponent(value) + '; path=/; expires=' + expires + '; SameSite=Lax';
    } catch (_e) {
      /* swallow */
    }
  }

  function getQueryParam(name) {
    try {
      const search = window.location.search || '';
      if (!search || search.length < 2) return null;
      const pairs = search.slice(1).split('&');
      for (let i = 0; i < pairs.length; i++) {
        const idx = pairs[i].indexOf('=');
        if (idx === -1) continue;
        const k = decodeURIComponent(pairs[i].slice(0, idx));
        if (k === name) {
          try {
            return decodeURIComponent(pairs[i].slice(idx + 1).replace(/\+/g, ' '));
          } catch (_e) {
            return null;
          }
        }
      }
    } catch (_e) {
      /* swallow */
    }
    return null;
  }

  function readLog() {
    const raw = readCookie(COOKIE_NAME);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch (_e) {
      /* swallow */
    }
    return [];
  }

  function appendLog(query) {
    if (!query || typeof query !== 'string') return null;
    const trimmed = query.trim().toLowerCase();
    if (!trimmed || trimmed.length < 2 || trimmed.length > 100) return null;
    const log = readLog();
    const now = Date.now();
    // Dedup: if query already present in last 24h, skip (avoid double-count
    // from refresh / back-button).
    for (let i = 0; i < log.length; i++) {
      if (log[i] && log[i].q === trimmed && now - (log[i].ts || 0) < 86400000) return null;
    }
    log.push({ q: trimmed, ts: now });
    // Cap size — drop oldest.
    while (log.length > MAX_ENTRIES) log.shift();
    let serialized;
    try {
      serialized = JSON.stringify(log);
    } catch (_e) {
      return null;
    }
    writeCookie(COOKIE_NAME, serialized);
    return serialized;
  }

  function syncToCartAttribute(serialized) {
    if (!serialized || !window.CoraCartAttributes) return;
    try {
      const attributes = {};
      attributes[CART_ATTR_KEY] = serialized;
      // The shared writer (snippets/cart-attribute-sync.liquid) serializes cart writes and posts only changed keys
      window.CoraCartAttributes.write(attributes, { keepalive: true }).catch(function () {
        /* swallow — best effort */
      });
    } catch (_e) {
      /* swallow */
    }
  }

  function init() {
    try {
      // Only fire on /search pages (covers both ?q= and ?type=product&q=)
      const path = (window.location.pathname || '').toLowerCase();
      if (path.indexOf('/search') === -1) return;
      const q = getQueryParam('q');
      if (!q) return;
      const serialized = appendLog(q);
      if (serialized) syncToCartAttribute(serialized);
    } catch (_e) {
      /* swallow */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
